// OWNED BY the documents implementation agent (see ../DocumentsTab.tsx ownership note).
//
// React bindings for the documents tab: render instantly from the session
// cache (warmed by preloadDocumentsTab on contacts-table row hover / page
// mount), kept live by the subcollection onSnapshot listener that writes back
// into the same cache key (so the tab-strip badge updates too).

import { useEffect, useState } from "react";
import {
  fetchCached,
  getCached,
  subscribeCached,
  useSessionCacheValue
} from "../../data/sessionCache";
import {
  DOCUMENTS_MAX_AGE_MS,
  SIGNATURES_MAX_AGE_MS,
  contactDocumentsKey,
  contactSignaturesKey,
  completedSignatureContracts,
  fetchContactDocumentsOnce,
  subscribeToContactDocuments
} from "./documentsData";
import { loadContactSignatures } from "../signatures/signaturesData";
import type { ContactDocumentWithId } from "./documentsData";
import type { RealEstateContact } from "../../../../../../@types/sharedSchema";
import type { SignatureDocument } from "../../../../../../@types/signatures";

type ContactDocumentsState = {
  documents: ContactDocumentWithId[] | undefined;
  loading: boolean;
  error: Error | null;
  /** Invalidate + refetch — the tab's retry affordance after a failed load. */
  refresh: () => void;
};

export function useContactDocuments(contact: RealEstateContact): ContactDocumentsState {
  const contactId = contact?.id ?? "";

  const { data, loading, error, refresh } = useSessionCacheValue<ContactDocumentWithId[]>(
    contactDocumentsKey(contactId),
    () => fetchContactDocumentsOnce(contactId),
    { maxAgeMs: DOCUMENTS_MAX_AGE_MS }
  );

  useEffect(() => {
    if (!contactId) return undefined;
    return subscribeToContactDocuments(contactId);
  }, [contactId]);

  return {
    documents: data,
    // "loading" only on a true cold start — a warm cache renders instantly.
    loading: loading && data === undefined,
    error,
    refresh
  };
}

/**
 * COMPLETED signature documents for the "חוזים חתומים" group, read from the
 * SHARED `signatures:{contactId}` cache key via the signatures tab's CANONICAL
 * fetcher (one query identity + sort + 403 policy for the shared key).
 * `enabled` gates the fetch on the "signature" feature flag (the API 403s
 * without it) — the subscription stays on regardless so a warm value written
 * by the signatures tab still renders.
 */
export function useContactSignatureContracts(
  contact: RealEstateContact,
  enabled: boolean
): SignatureDocument[] {
  const contactId = contact?.id ?? "";
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!contactId) return undefined;
    const unsubscribe = subscribeCached(contactSignaturesKey(contactId), () =>
      forceRender((n) => n + 1)
    );
    if (enabled) {
      void fetchCached(
        contactSignaturesKey(contactId),
        () => loadContactSignatures(contact),
        { maxAgeMs: SIGNATURES_MAX_AGE_MS }
      ).catch(() => undefined);
    }
    return unsubscribe;
    // `contact` is deliberately not a dep — contactId is the identity (same
    // convention as useSessionCacheValue's inline fetcher closures).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId, enabled]);

  return completedSignatureContracts(
    getCached<SignatureDocument[]>(contactSignaturesKey(contactId))
  );
}
